import { type DatasourceDataResponse } from '@atlaskit/linking-types';
import { AnyAri } from '@atlassian/ari/any-ari';
import { type CloudId } from '@databases/types/confluence';
import { loadAsync } from '@databases/utils/redux/redux';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { type DatasourceType } from '@canvas/atlassian-service/datasource';

import {
  type ConnectionContentWithColumns,
  selectContentOptional,
  selectContentStatus
} from './selectors.ts';
import connectionsSlice, { type Content, type ContentItems } from './slice.ts';
import { type DefaultRootState } from './types.ts';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

type ResolveConnectionContentFn = (
  ari: string,
  columns: string[],
  type: DatasourceType,
  cloudId: CloudId,
  connectionId: string
) => Promise<{ connectionId: string; datasourceResponse: DatasourceDataResponse }[]>;

let _resolveConnectionContent: ResolveConnectionContentFn | undefined;

/**
 * Must be called before any code path triggers `loadContent`.
 * Currently wired at app startup in `frontend/src/AppWithContext.tsx`.
 */
export function registerResolveConnectionContent(fn: ResolveConnectionContentFn): void {
  _resolveConnectionContent = fn;
}

export const loadContent = ({
  ari,
  connectionId,
  columns,
  forceReload
}: {
  ari: string;
  connectionId?: string | undefined;
  columns?: string[] | undefined;
  forceReload?: boolean;
}): Dispatcher<Promise<ConnectionContentWithColumns | undefined>> => {
  return async (dispatch, getState) => {
    const state = getState().connections;
    const content: Content | undefined = state.content[ari];
    if (!connectionId && content) {
      connectionId = state.content[ari]['connectionId'];
    }
    if (!connectionId) {
      return undefined;
    }

    const connection = state.connections[connectionId];
    if (!connection) {
      return undefined;
    }

    await loadAsync(
      () => selectContentStatus(getState().connections, ari),
      () => {
        dispatch(connectionsSlice.actions.loadContentPending({ ari }));
      },
      () => {
        if (!_resolveConnectionContent) {
          throw new Error(
            'resolveConnectionContent not registered. Call registerResolveConnectionContent at app startup.'
          );
        }
        return _resolveConnectionContent(
          ari,
          columns ?? connection.requiredColumns,
          'confluence',
          AnyAri.parse(ari).cloudId,
          connectionId ?? ''
        );
      },
      (responses) => {
        if (responses) {
          responses.forEach(({ connectionId, datasourceResponse }) => {
            datasourceResponse.data.items.forEach((item) => {
              dispatch(
                connectionsSlice.actions.addContentToList({
                  ari: item.id.data.toString(),
                  connectionId,
                  item: item as ContentItems,
                  columns: datasourceResponse.data.schema?.properties || []
                })
              );
              dispatch(
                connectionsSlice.actions.loadContentFinished({
                  connectionId,
                  ari: item.id.data.toString(),
                  access: datasourceResponse.meta.access
                })
              );
            });
          });
        }
      },
      (error) => {
        dispatch(connectionsSlice.actions.loadContentError({ ari, error }));
      },
      { forceReload }
    );

    return selectContentOptional(state, ari);
  };
};
